import { createSelector } from "reselect"
import { chain, get, reduce, isNil, map, mapValues, set, omit, isEmpty, pick, size, clone, floor, clamp, filter, slice } from "lodash"
import { getGroupingOptions, getIsActive, getSelectedMetricType, getRawSort, getSelectedGroup, getSelectedSubView } from "."
import { getIsNoLongerHeldFromContributor, getTickerAndMicFromContributor, isUnmodeled } from "helpers/securities"
import { getColumns } from "./columns"
import { FORMATS, makeCustomFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { Creators as AssetAnalyticsCreators } from "../model"
import { createQueryStringSelector } from "selectors/queryString"
import { buildContributorKey, extractContributorRisk } from "helpers/contributors"
import { computeGroupsMap, contributorsReducer, mergeContributorsMetrics, applySort, computeIdColumnPath, isCashOffsetContributor, extractContributorRiskFactor, computeAggregatedColumnTotals, getAssetsReviewColumnsByCategories, extractContributorComposition } from "helpers/assets"
import { getResidualTooltipContents } from "helpers/assets/tooltips"
import { computeFactorSensitivityMap, computeGroupedFactorSensitivityMap } from "helpers/factorSensitivity"
import { getAssetType } from "modules/analyze-composite-security/selectors"
import { computeGroupedMarginalContributionMap, computeMarginalContributionMap } from "helpers/marginalContribution"
import { getCategories } from "selectors/factors"
import { getSelectedView } from "selectors/assets"

const ITEMS_PER_PAGE = 200
const EMPTY_ARRAY = []

const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })

const getRawSelectedPage = createQueryStringSelector("page")
const getRawPositions = state => get(state, "global.data.compositeAssetAnalyticsComposition.data.model.simulation.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedPositions = state => get(state, "global.data.compositeAssetAnalyticsGroupedComposition.data.model.simulation.composition.0.compositionBy", EMPTY_ARRAY)
const getRawPerformanceContributors = state => get(state, "global.data.compositeAssetAnalyticsPerformance.data.model.simulation.performanceContributors", EMPTY_ARRAY)
const getRawGroupedPerformanceContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedPerformance.data.model.simulation.groupedPerformanceContributors", EMPTY_ARRAY)
const getRawRiskContributors = state => get(state, "global.data.compositeAssetAnalyticsRisk.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedRiskContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedRisk.data.model.simulation.groupedRiskContributors", EMPTY_ARRAY)
const getRawRiskAttribution = state => get(state, "global.data.compositeAssetAnalyticsRisk.data.model.simulation.risk.0.total")
const getRawBetaContributors = state => get(state, "global.data.compositeAssetAnalyticsBeta.data.model.simulation.betaContributors.0.contributors")
const getRawGroupedBetaContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedBeta.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getRawComposition = state => get(state, "global.data.compositeAssetAnalyticsComposition.data.model.simulation.composition.0")
const getRawCompositionDollars = state => get(state, "global.data.compositeAssetAnalyticsComposition.data.model.compositionDollar.composition.0.positions", EMPTY_ARRAY)
const getRawGroupedCompositionDollars = state => get(state, "global.data.compositeAssetAnalyticsGroupedComposition.data.model.compositionDollar.composition.0.compositionBy", EMPTY_ARRAY)
const getRawExposureContributors = state => get(state, "global.data.compositeAssetAnalyticsExposure.data.model.simulation.exposureContributors.0.contributors", EMPTY_ARRAY)
const getRawGroupedExposureContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedExposure.data.model.simulation.groupedExposureContributors.0.groupedContributors")
const getRawRiskFactorsContributors = state => get(state, "global.data.compositeAssetAnalyticsRiskFactors.data.model.simulation.riskContributors")
const getRawGroupedRiskFactorsContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedRiskFactors.data.model.simulation.groupedRiskContributors")
const getRawFactorSensitivityContributors = state => get(state, "global.data.assetAnalyticsFactorSensitivity.data.model.simulation.factorSensitivityContributors", EMPTY_ARRAY)
const getRawGroupedFactorSensitivityContributors = state => get(state, "global.data.assetAnalyticsGroupedFactorSensitivity.data.model.simulation.groupedFactorSensitivityContributors", EMPTY_ARRAY)
const getRawMarginalContributionContributors = state => get(state, "global.data.compositeAssetAnalyticsMarginalContribution.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getRawGroupedMarginalContributionContributors = state => get(state, "global.data.compositeAssetAnalyticsGroupedMarginalContribution.data.model.simulation.groupedRiskContributors", EMPTY_ARRAY)
const getShowNotHeld = (_, props) => get(props, "showNotHeld")
const getSegment = (_, props) => get(props, "segment")
export const getCompositionStatus = state => get(state, "global.data.compositeAssetAnalyticsComposition.status")
export const getEndDate = (_, props) => get(props, "endDate")

const sumEconomicExposureReducer = (acc, contributor) => {
  const key = buildContributorKey(contributor)
  if (acc[key]) {
    acc[key] += contributor.economicExposure
  } else {
    acc[key] = clone(contributor.economicExposure)
  }
  return acc
}

const getEconomicExposuresMap = createSelector(
  [getRawCompositionDollars],
  (positions) => chain(positions)
    .reduce(contributorsReducer, {})
    .mapValues(item => item.economicExposure)
    .value(),
)

const getGroupedEconomicExposuresMap = createSelector(
  [getRawGroupedCompositionDollars],
  (positions) =>
    reduce(positions, (acc, { id, economicExposure }) => set(acc, id, economicExposure), {}),
)

const getGroupedEconomicExposuresContributorsMap = createSelector(
  [getRawGroupedCompositionDollars],
  (positions) => chain(positions)
    .keyBy("id")
    .mapValues(group => chain(group)
      .get("contributors")
      .reduce(sumEconomicExposureReducer, {})
      .value())
    .value(),
)

const getContributorId = (contributor) => !isEmpty(contributor.id) ? contributor.id : "Unknown"

const extractContributorPerformance = (contributor) => {
  const attributionSummary = get(contributor, "attribution.summary")

  let total = get(contributor, "totalReturn.cumulativePercent")
  const residual = get(attributionSummary, "residualReturn.cumulativePercent")
  if (!isNil(residual)) {
    total = {
      value: total,
      tooltipContents: getResidualTooltipContents(percentageFormatter(residual)),
    }
  }

  return {
    ...omit(contributor, ["attribution", "totalReturn"]),
    performance: {
      total,
      factors: get(attributionSummary, "factorsReturn.cumulativePercent"),
      specific: get(attributionSummary, "specificReturn.cumulativePercent"),
      trading: get(attributionSummary, "tradingReturn.cumulativePercent"),
      residual,
    },
  }
}

const extractContributorBeta = (contributor) => ({
  beta: {
    ...pick(contributor, ["predicted", "historical"]),
    standalone: get(contributor, "securityBeta"),
  },
})

const extractContributorExposure = (contributor) => ({
  exposure: reduce(contributor.factors, (acc, factor) => set(acc, factor.id, get(factor, "exposure")), {}),
})

const getCompositionMap = createSelector(
  [getRawPositions, getEconomicExposuresMap, getRawGroupedPositions, getGroupedEconomicExposuresMap, getGroupingOptions],
  (positions, expMap, groupedPositions, groupedExpMap, groupingOptions) => {
    const contributors = groupingOptions ? groupedPositions : positions
    const exposureMap = groupingOptions ? groupedExpMap : expMap

    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorComposition(contributor, exposureMap)), {})
  },
)

const getGroupedCompositionContributorsMap = createSelector(
  [getRawGroupedPositions, getGroupedEconomicExposuresContributorsMap],
  (groups, exposureMap) => computeGroupsMap(groups, { mapFn: extractContributorComposition, exposureMap }),
)

const getPerformanceMap = createSelector(
  [getRawPerformanceContributors, getRawGroupedPerformanceContributors, getGroupingOptions],
  (performanceContributors, groupedPerformanceContributors, groupingOptions) => {
    const contributors = groupingOptions ? groupedPerformanceContributors : performanceContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorPerformance(contributor)), {})
  },
)

const getGroupedPerformanceContributorsMap = createSelector(
  [getRawGroupedPerformanceContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorPerformance }),
)

const getRiskMap = createSelector(
  [getRawRiskContributors, getRawGroupedRiskContributors, getGroupingOptions],
  (riskContributors, groupedRiskContributors, groupingOptions) => {
    const contributors = !groupingOptions ? riskContributors : groupedRiskContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRisk(contributor)), {})
  },
)

const getGroupedRiskContributorsMap = createSelector(
  [getRawGroupedRiskContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorRisk }),
)

const getBetaMap = createSelector(
  [getRawBetaContributors, getRawGroupedBetaContributors, getGroupingOptions],
  (betaContributors, groupedBetaContributors, groupingOptions) => {
    const contributors = !groupingOptions ? betaContributors : groupedBetaContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorBeta(contributor)), {})
  },
)

const getGroupedBetaContributorsMap = createSelector(
  [getRawGroupedBetaContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorBeta }),
)

const getExposureMap = createSelector(
  [getRawExposureContributors, getRawGroupedExposureContributors, getGroupingOptions],
  (exposureContributors, groupedExposureContributors, groupingOptions) => {
    const contributors = !groupingOptions ? exposureContributors : groupedExposureContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorExposure(contributor)), {})
  },
)

const getGroupedExposureContributorsMap = createSelector(
  [getRawGroupedExposureContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorExposure }),
)

const getRiskFactorsMap = createSelector(
  [getRawRiskFactorsContributors, getRawGroupedRiskFactorsContributors, getGroupingOptions],
  (riskFactorsContributors, groupedRiskFactorsContributors, groupingOptions) => {
    const contributors = !groupingOptions ? riskFactorsContributors : groupedRiskFactorsContributors
    return reduce(contributors, (acc, contributor) =>
      set(acc, buildContributorKey(contributor), extractContributorRiskFactor(contributor)), {})
  },
)

const getGroupedRiskFactorsContributorsMap = createSelector(
  [getRawGroupedRiskFactorsContributors],
  (groups) => computeGroupsMap(groups, { mapFn: extractContributorRiskFactor }),
)

const getFactorSensitivityMap = createSelector(
  [getRawFactorSensitivityContributors, getRawGroupedFactorSensitivityContributors, getGroupingOptions],
  computeFactorSensitivityMap,
)

const getGroupedFactorSensitivityMap = createSelector(
  [getRawGroupedFactorSensitivityContributors],
  computeGroupedFactorSensitivityMap,
)

const getMarginalContributionMap = createSelector(
  [getRawMarginalContributionContributors, getRawGroupedMarginalContributionContributors, getGroupingOptions],
  computeMarginalContributionMap,
)

const getGroupedMarginalContributionMap = createSelector(
  [getRawGroupedMarginalContributionContributors],
  computeGroupedMarginalContributionMap,
)

export const getReferenceEquity = createSelector(
  [getRawComposition],
  (composition) => get(composition, "referenceEquity", get(composition, "gmv")),
)

// Non basket assets don't have economic exposure, so we need to null it out
// PositionTooltip uses these values for display
function getAdjustedEconomicExposure(assetType, item) {
  return { economicExposure: assetType !== "basket" ? null : get(item, "economicExposure") }
}

const makeComputeContributorData = (assetType) => (contributor) => ({
  id: getContributorId(contributor),
  ...getTickerAndMicFromContributor(contributor),
  ...omit(contributor, ["id", "economicExposure"]),
  ...getAdjustedEconomicExposure(assetType, contributor),
  isUnmodeled: isUnmodeled(contributor),
  isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
  isNotHeld: get(contributor, "notHeld"),
})

const makeComputeGroupData = (assetType) => (item) => ({
  ...omit(item, ["economicExposure"]),
  ...getAdjustedEconomicExposure(assetType, item),
  label: item.name,
  isGroup: true,
})

export const getDataMap = createSelector(
  [getCompositionMap, getPerformanceMap, getRiskMap, getBetaMap, getExposureMap, getRiskFactorsMap, getFactorSensitivityMap, getMarginalContributionMap],
  mergeContributorsMetrics,
)

const getGroupContributorsDataMap = createSelector(
  [getGroupedCompositionContributorsMap, getGroupedPerformanceContributorsMap, getGroupedRiskContributorsMap, getGroupedBetaContributorsMap, getGroupedExposureContributorsMap, getGroupedRiskFactorsContributorsMap, getGroupedFactorSensitivityMap, getGroupedMarginalContributionMap],
  mergeContributorsMetrics,
)

const computeGroupAggregatedColumns = (label, group, columns) => reduce(columns, (acc, { path }) =>
  set(acc, path, get(group, path)), {
  id: label,
  isAggregate: true,
})

const getCurrentData = createSelector(
  [getDataMap, getGroupContributorsDataMap, getSelectedGroup, getGroupingOptions, getShowNotHeld, getIsActive, getColumns, getAssetType, getSelectedMetricType],
  (data, groupContributorsData, selectedGroup, groupingOptions, showNotHeld, isActive, columns, assetType, metricType) => {
    if (groupingOptions && !selectedGroup) {
      return map(data, makeComputeGroupData(assetType))
    }
    if (selectedGroup) {
      data = groupContributorsData[selectedGroup]
    }

    const contributors = chain(data)
      .filter(contributor => showNotHeld || !contributor.notHeld)
      .map(makeComputeContributorData(assetType))
      .value()

    if (!showNotHeld && isActive) {
      const notHeldContributors = chain(data)
        .filter(contributor => contributor.notHeld)
        .map(makeComputeContributorData(assetType))
        .value()
      if (size(notHeldContributors) > 0) {
        contributors.push({ ...computeAggregatedColumnTotals("NOT HELD", notHeldContributors, columns, metricType), isNotHeld: true })
      }
    }

    return contributors
  },
)

const getIdColumnPath = createSelector(
  [getGroupingOptions, getSelectedGroup],
  computeIdColumnPath,
)

export const getOrderedSecurities = createSelector(
  [getCurrentData, getRawSort, getColumns, getIdColumnPath],
  applySort,
)

export const getOrderedSecuritiesWithoutCashOffset = createSelector(
  [getOrderedSecurities],
  securities => filter(securities, security => !isCashOffsetContributor(security)),
)

export const getSelectedPage = createSelector(
  [getOrderedSecuritiesWithoutCashOffset, getRawSelectedPage],
  (securities, page) => {
    const count = size(securities)
    const pageCount = floor(count / ITEMS_PER_PAGE)
    return clamp(page ? page - 1 : 0, 0, pageCount)
  },
)

export const getSecurities = createSelector(
  [getOrderedSecuritiesWithoutCashOffset, getSelectedPage],
  (securities, selectedPage) => {
    const start = selectedPage * ITEMS_PER_PAGE
    return slice(securities, start, start + ITEMS_PER_PAGE)
  },
)

const getAvailableGroupsList = createSelector(
  [getCompositionMap, getPerformanceMap],
  (compositionGroups, performanceGroups) => mapValues(
    {
      ...performanceGroups,
      ...compositionGroups,
    },
    (group) => group.name,
  ),
)

export const getSelectedGroupName = createSelector(
  [getSelectedGroup, getAvailableGroupsList],
  (selectedGroup, groups) => get(groups, selectedGroup, selectedGroup),
)

export const getAggregatedData = createSelector(
  [getOrderedSecurities, getRawRiskAttribution, getColumns, getSelectedGroup, getSelectedGroupName, getSegment, getDataMap, getCategories, getSelectedView, getSelectedSubView, getSelectedMetricType],
  (securities, riskAttribution, columns, selectedGroup, selectedGroupName, segment, dataMap, categories, selectedView, selectedSubView, metricType) => {
    const isDrillDown = !!selectedGroup
    let aggregatedData
    if (isDrillDown) {
      aggregatedData = computeGroupAggregatedColumns(selectedGroupName, dataMap[selectedGroup], columns, selectedView, categories, selectedSubView)
    } else {
      aggregatedData = computeAggregatedColumnTotals(segment ? "SEGMENT" : "TOTAL", securities, columns, metricType)
    }

    const totalRisk = get(aggregatedData, "risk.total")
    if (!isDrillDown && !isNil(totalRisk)) {
      aggregatedData.risk.total = {
        value: totalRisk,
        tooltipContents: [`Total Risk: ${numberFormatter(riskAttribution * 100)}%`],
      }
    }

    const residual = get(aggregatedData, "performance.residual")
    if (!isDrillDown && !isNil(residual)) {
      aggregatedData.performance.total = {
        value: aggregatedData.performance.total,
        tooltipContents: getResidualTooltipContents(percentageFormatter(residual)),
      }
    }

    return {
      ...aggregatedData,
      isDrillDown,
      action: AssetAnalyticsCreators.setSelectedGroup,
    }
  },
)

export const getColumnsByCategories = createSelector(
  [getColumns, getCurrentData, getSelectedSubView, getSelectedView, getSelectedMetricType, getCategories],
  getAssetsReviewColumnsByCategories,
)

export const getQueries = createSelector(
  [getColumns],
  (columns) => reduce(columns, (acc, column) => {
    acc[column.query] = true
    return acc
  }, {}),
)
